import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";

function encode(data) {
  return Object.keys(data)
    .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k]))
    .join("&");
}

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "contacto", ...form }),
      });
      if (!res.ok && res.status !== 200) throw new Error("Error de red");
      toast.success("Mensaje enviado. Te responderé pronto.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("No se pudo enviar. Inténtalo más tarde.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen pt-32 pb-24 px-6 md:px-12">
      <div className="max-w-7xl mx-auto grid grid-cols-12 gap-12">
        <div className="col-span-12 md:col-span-5 reveal">
          <p className="eyebrow mb-5">Hablemos</p>
          <h1 className="font-serif-display text-paper text-5xl sm:text-6xl leading-[0.95] tracking-tight">
            Escríbeme<br />
            <span className="italic text-paper-dim">unas líneas.</span>
          </h1>
          <p className="mt-8 text-paper-dim font-light leading-relaxed max-w-sm">
            Si una imagen te dijo algo, si quieres hablar de fotografía,
            colaborar en un proyecto o simplemente saludar — escríbeme.
            Leo cada mensaje.
          </p>

          <div className="mt-12 space-y-6">
            <div>
              <div className="eyebrow mb-1">Estudio</div>
              <div className="font-serif-display text-paper text-xl">Estudio Sombra</div>
              <div className="font-mono-ed text-xs text-paper-mute mt-1">por Paco Revueltas</div>
            </div>
            <div>
              <div className="eyebrow mb-1">Instagram</div>
              <a
                href="https://www.instagram.com/pacorevueltas/"
                target="_blank"
                rel="noreferrer noopener"
                className="font-light text-paper hover:text-paper-dim transition-colors underline underline-offset-4 decoration-ink-600 hover:decoration-paper"
                data-testid="contact-instagram"
              >
                @pacorevueltas
              </a>
            </div>
            <div>
              <div className="eyebrow mb-1">Portafolio anterior</div>
              <a
                href="https://pacorevueltas.com"
                target="_blank"
                rel="noreferrer noopener"
                className="font-light text-paper hover:text-paper-dim transition-colors underline underline-offset-4 decoration-ink-600 hover:decoration-paper"
                data-testid="contact-current-site"
              >
                pacorevueltas.com
              </a>
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-7 reveal">
          {/* Netlify Forms — the form-name attr + hidden input are required */}
          <form
            name="contacto"
            method="POST"
            data-netlify="true"
            netlify-honeypot="bot-field"
            onSubmit={submit}
            className="space-y-2"
            data-testid="contact-form"
          >
            <input type="hidden" name="form-name" value="contacto" />
            <p className="hidden">
              <label>No rellenar: <input name="bot-field" /></label>
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              <Field label="Nombre" required>
                <input
                  type="text" name="name" required
                  value={form.name} onChange={update("name")}
                  className="input-ed" placeholder="Tu nombre"
                  data-testid="contact-input-name"
                />
              </Field>
              <Field label="Email" required>
                <input
                  type="email" name="email" required
                  value={form.email} onChange={update("email")}
                  className="input-ed" placeholder="tu@correo.com"
                  data-testid="contact-input-email"
                />
              </Field>
            </div>

            <Field label="Asunto" required>
              <input
                type="text" name="subject" required
                value={form.subject} onChange={update("subject")}
                className="input-ed" placeholder="Sobre qué quieres hablar"
                data-testid="contact-input-subject"
              />
            </Field>

            <Field label="Mensaje" required>
              <textarea
                required rows={6} name="message"
                value={form.message} onChange={update("message")}
                className="input-ed resize-none"
                placeholder="Cuéntame brevemente lo que tengas en mente."
                data-testid="contact-input-message"
              />
            </Field>

            <div className="pt-8 flex items-center justify-between gap-4 flex-wrap">
              <p className="font-mono-ed text-xs text-paper-mute max-w-xs">
                Al enviar aceptas que use tu email únicamente para responderte.
              </p>
              <button type="submit" disabled={submitting} className="btn-primary" data-testid="contact-submit">
                {submitting ? "Enviando…" : <>Enviar <Send size={14} strokeWidth={1.5} /></>}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="py-3">
      <div className="eyebrow mb-1">
        {label}{required && <span className="text-paper-mute"> *</span>}
      </div>
      {children}
    </div>
  );
}
